import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { request } from 'app/utils';
import { AxiosError } from 'axios';
import { Button, Flex, Icons, Loading, Page, Typography } from 'components';
import Alert, { useAlert } from 'features/alert';
import { CenteredLayout, useEducational } from 'features/educational';
import { useSchool } from 'features/school';
import { EducationalInstitutionRecoverUserByActivationCodeResponse } from '../types/EducationalInstitutionRecoverUserByActivationCodeResponse';

const ArrowIcon = styled(Icons)`
  width: 24px;
  color: ${({ theme }) => theme.colors.white};
`;

const Input = styled.input`
  border: 2px solid ${({ theme }) => theme.colors.gray_100};
  border-radius: 4px;
  padding: 18px;
  font-size: 20px;
  text-align: center;
  letter-spacing: 3px;
  outline: none;
  color: ${({ theme }) => theme.colors.gray_600};
  font-weight: 500;
  width: 95%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray_300};
  }
`;

// const Button = styled.button`
//   border: 0;
//   border-radius: 4px;
//   padding: 15px 20px;
//   font-size: 18px;
//   text-align: center;
//   outline: none;
//   font-weight: 500;
//   background: ${({ theme }) => theme.colors.primary_400};
//   color: white;
//   cursor: pointer;

//   &:hover {
//     background: ${({ theme }) => theme.colors.primary_200};
//   }
// `;

const RecoveryUser = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'code' | 'pin'>('code');
  const [fields, setFields] = useState<{ activationCode: string; pin: string }>({ activationCode: '', pin: '' });

  const { schoolUuid } = useSchool();
  const { setRecoveryUser } = useEducational();
  const { setAlert, clearAlert } = useAlert();

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      clearAlert();
      setLoading(true);

      await request<EducationalInstitutionRecoverUserByActivationCodeResponse>({
        url: '/api/v1/School/EducationalInstitution/TryRecoverUser',
        method: 'POST',
        data: {
          schoolUuid,
          ...fields,
        },
      })
        .then(({ data }) => {
          setRecoveryUser(data);

          // navigate(createUrl('educational/register/switcher'));
        })
        .catch((error: AxiosError<{ errorMessage?: string }>) => {
          setAlert({ type: 'error', message: error.response?.data?.errorMessage || 'Ocorreu um erro ao recuperar usuário.' });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [schoolUuid, fields]
  );

  return (
    <>
      <Page title="MELVER nas Escolas">
        {loading && <Loading alpha />}

        <CenteredLayout>
          <form onSubmit={onSubmit}>
            {step === 'code' && (
              <Flex flexDirection="column" alignItems="center" style={{ gap: 20 }}>
                <Flex flexDirection="column" alignItems="center" textAlign="center" style={{ gap: 5 }}>
                  <Typography type="type5" fontWeight="bold">
                    Informe seu código de registro
                  </Typography>

                  <Typography type="type3">
                    O código está colado no verso do seu material didático.
                    <br />
                    Ele é composto por letras e números.
                  </Typography>
                </Flex>

                <Flex width={1} justifyContent={'center'}>
                  <Input
                    placeholder="RD-ESC-TURM-00000000"
                    value={fields.activationCode}
                    onChange={(e) => setFields(() => ({ ...fields, activationCode: e.target.value }))}
                    autoFocus
                  />
                </Flex>

                <Alert invert px={0} py={0} style={{ fontWeight: 'bold' }} />

                <Flex>
                  <Button
                    onClick={() => setStep('pin')}
                    py={4}
                    width={1}
                    px={3}
                    color="white"
                    bg={fields.activationCode ? 'primary_400' : 'gray_400'}
                    justifyContent="space-between"
                    style={{ gap: 20, opacity: fields.activationCode ? 1 : 0.8 }}
                    disabled={!fields.activationCode}
                  >
                    <>Continuar</>
                    <ArrowIcon icon="arrowRightThin" />
                  </Button>
                </Flex>
              </Flex>
            )}

            {step === 'pin' && (
              <Flex flexDirection="column" alignItems="center" style={{ gap: 20 }}>
                <Flex flexDirection="column" alignItems="center" textAlign="center" style={{ gap: 5 }}>
                  <Typography type="type5" fontWeight="bold">
                    Agora digite seu PIN
                  </Typography>

                  <Typography type="type3">
                    Seu PIN está colado no verso do material didático.
                    <br />
                    Ele é composto por 6 números.
                  </Typography>
                </Flex>

                <Flex width={1} justifyContent={'center'}>
                  <Input placeholder="12345" value={fields.pin} onChange={(e) => setFields(() => ({ ...fields, pin: e.target.value }))} autoFocus />
                </Flex>

                <Alert invert px={0} py={0} style={{ fontWeight: 'bold' }} />

                <Flex>
                  <Button
                    type="submit"
                    py={4}
                    width={1}
                    px={3}
                    color="white"
                    bg={fields.pin ? 'primary_400' : 'gray_400'}
                    justifyContent="space-between"
                    style={{ gap: 20, opacity: fields.pin ? 1 : 0.8 }}
                    disabled={!fields.pin}
                  >
                    <>Enviar</>
                    <ArrowIcon icon="arrowRightThin" />
                  </Button>
                </Flex>
              </Flex>
            )}
          </form>
        </CenteredLayout>
      </Page>
    </>
  );
};

export default RecoveryUser;
